import { ChangeEventHandler, useCallback, useState } from "react";
import { GeneralErrors } from "./useInputHandler";

export enum FileErrors {
  MinimumSize = "MinimumSize",
  MaximumSize = "MaximumSize",
  MediaType = "MediaType",
  MaxImageHeight = "MaxImageHeight",
}

export const FileValidators = {
  minimumSize: (minSize: number) => {
    return (file: File) =>
      file.size < minSize ? FileErrors.MinimumSize : undefined;
  },
  maximumSize: (maxSize: number) => {
    return (file: File) =>
      file.size > maxSize ? FileErrors.MaximumSize : undefined;
  },
  required: (file: File) => (!file ? GeneralErrors.Required : undefined),
  mediaType: (validTypes: string[]) => {
    return (file: File) =>
      !validTypes.includes(file.type) ? GeneralErrors.MediaType : undefined;
  },
  maxImageHeight: (maxHeight: number) => {
    return async (file: File) => {
      const url = URL.createObjectURL(file);
      let img = new Image();
      img.src = url;
      img.onload = function () {
        if (img.height > maxHeight) {
          URL.revokeObjectURL(img.src);
          return FileErrors.MaxImageHeight;
        } else {
          URL.revokeObjectURL(img.src);
          return undefined;
        }
      };
      // TODO: immediately returns undefined;
    };
  },
};

type ValidationRule<T = FileErrors> = (value: File) => T | undefined;

type Inputs = {
  initialValue?: File;
  rules?: ValidationRule<FileErrors | GeneralErrors>[];
  errorMessages?: {
    [key in string]: string;
  };
};

export type Outputs = {
  file: File | undefined;
  error: string | undefined;
  setError: (text: string) => void;
  onChange: ChangeEventHandler<HTMLInputElement>;
  setValue: (file: File | undefined) => void;
  required?: boolean; //used for disabling submit button
};
export const useFileHandler = (input?: Inputs): Outputs => {
  const [file, setFile] = useState<File | undefined>(input?.initialValue);
  const [error, setError] = useState<string | undefined>();
  const validate = useCallback((file: File): string | undefined => {
    if (!input?.rules) {
      return;
    }
    let err = undefined;
    for (const rule of input.rules) {
      err = rule(file);
      if (err) {
        break;
      }
    }
    return err;
  }, []);

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.target.files?.[0]) {
        const err = validate(event.target.files[0]);
        setError(input?.errorMessages?.[err as string]);
        if (!err) setFile(event.target.files[0]);
        else setFile(undefined);
      }
    },
    []
  );
  return {
    file,
    error,
    setError,
    onChange,
    required: input?.rules?.includes(FileValidators.required),
    setValue: setFile,
  };
};
